import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Button, Link, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import AskAiButton from './AskAiButton';
import { ackAttribution, adGroupHref, campaignLabel, IssueRef, resolutionText } from './issueRef';
import ruleLabel, { chipRedundant } from './ruleLabels';
import isIORule from './rules';
import { formatAge, formatAgo, SEVERITY_COLOR, SeverityDot, severitySoftBg } from './severity';

interface IssueRowProps {
  item: IssueRef;
  selected: boolean;
  onSelect: (item: IssueRef) => void;
  onAck: (issueId: number) => void;
  onAskAi: (item: IssueRef) => void;
  askAiEnabled: boolean;
  acking: boolean;
  // True on the Resolved tab: read-only, resolution reason instead of an Ack.
  readOnly: boolean;
  // False when the band shows one uniform trafficker on its header (rollup).
  showTrafficker: boolean;
  // False when every io-grain row under this IO shares one campaign — the sub-header
  // shows the "via" once instead.
  showCampaignAttr: boolean;
  // Height of everything sticky above a row (app sub-nav + band header) so a scrolled-to
  // row never lands underneath the band header.
  scrollMarginTop: number;
}

// IssueRow is one single-line ledger row: severity dot, title, the rule chip (dropped
// when it only repeats the title), the evidence excerpt inline, the entity link, owner,
// ages and the actions. The full evidence and the raw rule id live in the inspector,
// which is what clicking the row opens. IO-grain rows (io_*) carry no entity tail at
// all: the IO sub-header above them owns the insertion-order link, the daily-spend
// action and — when the rows agree on one — the "via <campaign>" attribution.
function IssueRow({
  item,
  selected,
  onSelect,
  onAck,
  onAskAi,
  askAiEnabled,
  acking,
  readOnly,
  showTrafficker,
  showCampaignAttr,
  scrollMarginTop,
}: IssueRowProps) {
  const { issue } = item;
  const isIO = isIORule(issue.rule_id);
  const isAcknowledged = issue.state === 'acknowledged';
  const attribution = ackAttribution(issue);
  const chipLabel = ruleLabel(issue.rule_id);
  const showChip = chipLabel.trim() !== '' && !chipRedundant(chipLabel, issue.title);
  const rowRef = React.useRef<HTMLDivElement>(null);

  // Selection can come from j/k or a tab switch, not just a click — pull the row clear
  // of the sticky stack. scrollIntoView is unimplemented in jsdom, hence the guard.
  React.useEffect(() => {
    if (selected) {
      rowRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [selected]);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const campaign = campaignLabel(issue);
  const viaText = isIO && showCampaignAttr && campaign ? `via ${campaign}` : '';

  return (
    <Box
      ref={rowRef}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      data-testid={`alert-row-${issue.id}`}
      onClick={() => onSelect(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(item);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        overflow: 'hidden',
        textAlign: 'left',
        scrollMarginTop: `${scrollMarginTop}px`,
        py: 0.75,
        pr: 1.5,
        pl: 3.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.08) : 'transparent',
        boxShadow: selected ? (t) => `inset 3px 0 0 ${t.palette.primary.main}` : 'none',
        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, selected ? 0.12 : 0.04) },
      }}
    >
      <SeverityDot severity={issue.severity} />

      {/* Title + chip + evidence share the elastic half of the row; everything to the
          right of them is fixed-width so the row can never outgrow its container. */}
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          noWrap
          sx={{ flex: '0 1 auto', minWidth: 0 }}
          title={`${issue.title} — ${chipLabel} (${issue.rule_id})`}
        >
          {issue.title}
        </Typography>

        {showChip && (
          <Tooltip title={`Rule id: ${issue.rule_id}`}>
            <Box
              component="span"
              data-testid={`alert-type-${issue.id}`}
              sx={{
                flex: 'none',
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.7,
                px: 0.75,
                borderRadius: 99,
                whiteSpace: 'nowrap',
                color: SEVERITY_COLOR[issue.severity],
                bgcolor: severitySoftBg(issue.severity),
              }}
            >
              {chipLabel}
            </Box>
          </Tooltip>
        )}

        {issue.evidence.trim() && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={issue.evidence}
            data-testid={`alert-evidence-excerpt-${issue.id}`}
            sx={{ flex: '1 1 0', minWidth: 0, fontFamily: 'monospace', fontSize: 11.5 }}
          >
            {issue.evidence}
          </Typography>
        )}
      </Stack>

      {viaText && (
        <Typography
          variant="caption"
          color="text.disabled"
          noWrap
          data-testid={`alert-campaign-attr-${issue.id}`}
          sx={{ flex: '0 1 auto', minWidth: 0 }}
        >
          {viaText}
        </Typography>
      )}

      {!isIO && (
        <Link
          component={RouterLink}
          to={adGroupHref(item)}
          variant="caption"
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          noWrap
          sx={{ flex: '0 1 auto', minWidth: 0 }}
        >
          {issue.campaign_name || `Ad group ${issue.campaign_id}`}
        </Link>
      )}

      {showTrafficker && (
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ flex: '0 1 auto', minWidth: 0 }}
          data-testid={`alert-trafficker-${issue.id}`}
        >
          <PersonOutlineIcon sx={{ fontSize: 13, flex: 'none' }} color="disabled" />
          <Typography variant="caption" color="text.disabled" noWrap>
            {issue.trafficker?.name ?? 'Unassigned'}
          </Typography>
        </Stack>
      )}

      <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ flex: 'none' }}>
        <Tooltip title={`First seen ${new Date(issue.first_seen).toLocaleString()}`}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
            {formatAge(issue.first_seen)}
          </Typography>
        </Tooltip>
        <Tooltip title={`Last seen ${new Date(issue.last_seen).toLocaleString()}`}>
          <Typography variant="caption" color="text.disabled" noWrap data-testid={`alert-last-seen-${issue.id}`}>
            · seen {formatAgo(issue.last_seen)}
          </Typography>
        </Tooltip>
      </Stack>

      {askAiEnabled && <AskAiButton onClick={() => onAskAi(item)} testId={`alert-ask-ai-${issue.id}`} />}

      {readOnly ? (
        <Tooltip title={resolutionText(issue)}>
          <Typography
            variant="caption"
            color="success.main"
            noWrap
            data-testid={`alert-resolution-${issue.id}`}
            // A floor under the ellipsis so the reason never cuts mid-word into
            // something unreadable; the tooltip carries the full text either way.
            sx={{ flex: '0 1 auto', minWidth: 92, maxWidth: 200 }}
          >
            {resolutionText(issue)}
          </Typography>
        </Tooltip>
      ) : (
        <>
          {isAcknowledged && attribution && (
            <Tooltip title={issue.acknowledged_at ? new Date(issue.acknowledged_at).toLocaleString() : ''}>
              <Typography
                variant="caption"
                color="text.disabled"
                noWrap
                data-testid={`alert-ack-attr-${issue.id}`}
                sx={{ flex: '0 1 auto', minWidth: 0, maxWidth: 160 }}
              >
                {attribution}
              </Typography>
            </Tooltip>
          )}
          <Button
            size="small"
            variant={isAcknowledged ? 'text' : 'outlined'}
            startIcon={isAcknowledged ? <CheckIcon /> : undefined}
            disabled={isAcknowledged || acking}
            onClick={(e) => {
              stop(e);
              onAck(issue.id);
            }}
            // Ack mutates state on click, so it keeps a full gap clear of Ask AI and
            // the ages beside it — no fat-finger reach from an adjacent affordance.
            sx={{ flex: 'none', ml: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {isAcknowledged ? 'Acknowledged' : 'Ack'}
          </Button>
        </>
      )}
    </Box>
  );
}

export default IssueRow;
